import { useQuery } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Badge, Button, IconArrowLeft, ListTable, Notice, type BadgeProps } from "@wilco/ui";
import { formatDate, formatMoney, t, type MessageKey } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { accountsQuery, transactionsQuery, type Transaction, type TxState } from "./api.ts";

const route = getRouteApi("/app/money/bank/accounts/$id");

const STATE: Record<TxState, { key: MessageKey; tone: BadgeProps["tone"] }> = {
  PENDING: { key: "bank.state.PENDING", tone: "warning" },
  SUPPLIER_PAYMENT: { key: "bank.state.SUPPLIER_PAYMENT", tone: "success" },
  CUSTOMER_RECEIPT: { key: "bank.state.CUSTOMER_RECEIPT", tone: "success" },
  OPEX: { key: "bank.state.OPEX", tone: "strong" },
  OWN_TRANSFER: { key: "bank.state.OWN_TRANSFER", tone: "neutral" },
  IGNORED: { key: "bank.state.IGNORED", tone: "neutral" },
};

export function TxBadge({ state }: { state: string }) {
  // Новое состояние сервера, которого веб ещё не знает, показывается как «не зіставлено».
  const s = (STATE as Partial<Record<string, (typeof STATE)[TxState]>>)[state] ?? STATE.PENDING;
  return <Badge tone={s.tone}>{t(s.key)}</Badge>;
}

/** Транзакции счёта: что пришло из банка и чем каждая стала. */
export function AccountPage() {
  const { id } = route.useParams();
  const accountId = Number(id);
  const accounts = useQuery(accountsQuery);
  const list = useQuery(transactionsQuery(accountId));
  const account = accounts.data?.find((a) => a.id === accountId);
  // Сервер отдаёт новые сверху.
  const rows = list.data ?? [];

  return (
    <div className="page money">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/money/bank" search={{ tab: "accounts" }} aria-label={t("bank.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title">{account?.name ?? t("bank.transactions")}</h1>
        <Button asChild variant="secondary">
          <Link to="/money/bank/accounts/$id/edit" params={{ id }}>
            {t("money.edit")}
          </Link>
        </Button>
      </div>
      {account ? <p className="w-muted w-num">{account.iban}</p> : null}
      {list.error ? <Notice tone="danger" title={errorNotice(list.error).title} /> : null}
      <ListTable<Transaction>
        label={t("bank.transactions")}
        rows={rows}
        rowKey={(r) => String(r.id)}
        empty={<p className="w-muted">{t("bank.transactions.empty")}</p>}
        columns={[
          { key: "who", header: t("bank.counterparty"), kind: "title", render: (r) => r.counterparty || r.purpose },
          { key: "purpose", header: t("bank.purpose"), kind: "text", render: (r) => r.purpose },
          { key: "date", header: t("bank.date"), kind: "text", width: "6rem", render: (r) => formatDate(r.date) },
          { key: "state", header: t("bank.state"), kind: "text", width: "10rem", render: (r) => <TxBadge state={r.state} /> },
          { key: "amount", header: t("bank.amount"), kind: "number", width: "10rem", render: (r) => formatMoney(r.amount, r.currency) },
        ]}
      />
    </div>
  );
}
